import React, { useState, useEffect } from "react";
import { MdClose } from "react-icons/md";
import { FaRegCheckSquare, FaRegSquare, FaCheckSquare } from "react-icons/fa";
import { CircularProgress } from "@mui/material";
import tw from "tailwind-styled-components";
import { Label, ModalHeader, ScrollableContent } from "./Styles/InputStyles";
import { GoSearch } from "react-icons/go";

const MultiPropSelectModal = ({
    isOpen,
    onClose,
    onSelect,
    options = [],
    selectedProperties = [],
    isLoading = false,
    width
}) => {
    const modalWidth = width ? `lg:w-[35%] md:w-5/12 w-11/12` : "w-11/12";
    const [tempSelectedProperties, setTempSelectedProperties] = useState(selectedProperties);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if (isOpen) {
            setTempSelectedProperties(selectedProperties);
            setSearchText("");
        }
    }, [isOpen, selectedProperties]);

    const handleSave = () => {
        onSelect(tempSelectedProperties);
        onClose();
    };

    const handlePropertyToggle = (propertyId) => {
        setTempSelectedProperties(prev =>
            prev.includes(propertyId)
                ? prev.filter(id => id !== propertyId)
                : [...prev, propertyId]
        );
    };

    const handleSelectAll = () => {
        if (tempSelectedProperties.length === filteredOptions.length) {
            setTempSelectedProperties([]);
        } else {
            setTempSelectedProperties(filteredOptions.map(prop => prop.id));
        }
    };

    const filteredOptions = options.filter(property =>
        property.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        property.gId?.toLowerCase().includes(searchText.toLowerCase())
    );

    const isSelected = (propertyId) => {
        return tempSelectedProperties.includes(propertyId);
    };

    const isAllSelected = filteredOptions.length > 0 && tempSelectedProperties.length === filteredOptions.length;
    const isIndeterminate = tempSelectedProperties.length > 0 && tempSelectedProperties.length < filteredOptions.length;

    if (!isOpen) return null;

    return (
        <Overlay>
            <ModalWrapper className={modalWidth}>
                <ModalHeader>
                    <h1 className="text-white text-lg font-medium">Select Properties</h1>
                    <button onClick={onClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </ModalHeader>

                <div className="px-4 pt-4 pb-2 relative">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search properties..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <GoSearch className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                </div>

                <ScrollableContent className="max-h-[calc(100dvh-200px)]">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    ) : (
                        <div className="px-4">
                            <OptionRow
                                onClick={handleSelectAll}
                                className="border-b border-gray-200 mb-2 pb-3"
                            >
                                <div className="flex items-center flex-1">
                                    <div className="mr-3">
                                        {isLoading ? (
                                            <CircularProgress size={20} />
                                        ) : isAllSelected ? (
                                            <FaCheckSquare size={20} className="text-green-500 font-normal text-sm" />
                                        ) : isIndeterminate ? (
                                            <div className="w-5 h-5 border-2 border-green-500 bg-green-500 bg-opacity-20 rounded flex items-center justify-center">
                                                <div className="w-2 h-2 bg-green-500 rounded-sm" />
                                            </div>
                                        ) : (
                                            <FaRegSquare size={20} className="text-gray-300 font-normal text-sm" />
                                        )}
                                    </div>
                                    <Label className="font-medium text-gray-600">Select All</Label>
                                </div>
                            </OptionRow>

                            <div className="space-y-1">
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map((property) => (
                                        <OptionRow
                                            key={property.id}
                                            onClick={() => handlePropertyToggle(property.id)}
                                        >
                                            <div className="flex items-center flex-1">
                                                <div className="mr-3">
                                                    {isLoading ? (
                                                        <CircularProgress size={20} />
                                                    ) : isSelected(property.id) ? (
                                                        <FaRegCheckSquare size={20} className="text-primary-color font-normal text-sm" />
                                                    ) : (
                                                        <FaRegSquare size={20} className="text-gray-300 font-normal text-sm" />
                                                    )}
                                                </div>
                                                <div className="flex flex-col flex-1">
                                                    <Label className="font-medium text-sm text-gray-600">
                                                        {property.name}
                                                    </Label>
                                                    {/* {property.gId && (
                                                        <span className="text-xs text-gray-500 mt-1">
                                                            ID: {property.gId}
                                                        </span>
                                                    )} */}
                                                </div>
                                            </div>
                                        </OptionRow>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        {options.length === 0 ? "No properties available" : "No properties found"}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </ScrollableContent>

                <ActionSection>
                    <SideCancelBtn type="button" onClick={onClose}>
                        Cancel
                    </SideCancelBtn>
                    <SubmitBtn
                        type="button"
                        onClick={handleSave}
                    >
                        Confirm ({tempSelectedProperties.length})
                    </SubmitBtn>
                </ActionSection>
            </ModalWrapper>
        </Overlay>
    );
};

// Styled components
const Overlay = tw.div`fixed inset-0 z-50 flex items-center justify-end bg-black/50`;
const ModalWrapper = tw.div`bg-white w-11/12 h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const OptionRow = tw.div`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 rounded-lg`;
const ActionSection = tw.div`flex gap-3 p-4 border-t border-gray-200 bg-white`;
const SideCancelBtn = tw.button`flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium`;
const SubmitBtn = tw.button`flex-1 py-3 px-4 bg-primary-color text-white rounded-lg hover:bg-hover-color font-medium`;

export default MultiPropSelectModal;